import { DataSetCustomLabel } from '$shared/constants/DataSetCustomLabel';
import { DatasetLabel } from '$shared/constants/DatasetLabel';
import { DatasetTag } from '$shared/constants/DatasetTag';
import { GlobalAppDataKey } from '$shared/constants/GlobalAppDataKey';
import { sentry } from '$shared/helpers/logger/sentry';
import type DatasetMeta from '$shared/models/DatasetMeta';
import type { VerificationMetadata } from '$shared/models/VerificationMetadata';
import { appData } from '.';
import { schemaData } from '../data/SchemaData';

export class VerificationData {
  static get data(): VerificationMetadata | undefined {
    return appData.getData<VerificationMetadata>(GlobalAppDataKey.VerificationMetadata);
  }

  static get MedicaidDataset(): string[] {
    return VerificationData.data?.getTypesByTag(DatasetTag.MedicaidEnrollment) || [];
  }

  static get BoardDataset(): string[] {
    return VerificationData.data?.getTypesByTag(DatasetTag.BoardCertifications) || [];
  }

  static get SanctionDataset(): string[] {
    return VerificationData.data?.getTypesByTag(DatasetTag.SanctionsExclusions) || [];
  }

  static get hasDatasetMetaData(): boolean {
    return !!(
      !Array.isArray(VerificationData.data?.datasetModels) ||
      VerificationData.data.datasetModels.length > 0
    );
  }

  public static getMetaData(dataset: string): DatasetMeta | undefined {
    if (!this.hasDatasetMetaData) {
      return;
    }
    return VerificationData.data?.datasetModels?.find((metaData) => metaData.type === dataset);
  }

  public static getDatasetFromName(name: string): string | undefined {
    return VerificationData.data?.datasetModels?.find(
      (metadata: DatasetMeta) => metadata.name === name || metadata.customLabel === name
    )?.type;
  }

  public static getDatasetTag(dataset: string): DatasetTag | undefined {
    if (!this.hasDatasetMetaData) {
      return;
    }
    return VerificationData.data?.datasetModels
      ?.find((metadata: DatasetMeta) => metadata.type === dataset)
      ?.tags?.first();
  }

  public static getDatasetName(dataset: string, customLabel = false): string {
    if (!dataset || !schemaData.Dataset[dataset] || !VerificationData.hasDatasetMetaData) {
      return '';
    }
    const metadata = VerificationData.data?.getDatasetMetadata(dataset);
    if (!metadata) {
      sentry.warn('Trying to fetch dataset metadata which is not present', {
        requested: dataset,
        metadata: VerificationData.data
      });
      return dataset;
    }
    let name = metadata.name;
    if (DatasetLabel[dataset] && metadata.name !== DatasetLabel[dataset]) {
      if (customLabel && DataSetCustomLabel[dataset]) {
        name = DataSetCustomLabel[dataset];
      } else {
        name = DatasetLabel[dataset];
      }
    }
    return name;
  }
}
